// formatters.js — Single source of truth for display formatting.
//
// RULES (no exceptions):
// - Currency: always "RM 1,234.56" with thousands separator; negatives "-RM 1,234.56".
// - Dates: always dd/mm/yyyy in tables, forms, and KPIs.
// - Never render raw DB slugs: format all enum values via formatEnumLabel.

/**
 * Format a number as Malaysian Ringgit currency: "RM 1,234.56" / "-RM 1,234.56".
 * null/undefined/NaN are treated as 0.
 */
export function formatCurrency(value) {
  const num = Number(value);
  const safe = Number.isFinite(num) ? num : 0;
  const abs = Math.abs(safe);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return safe < 0 ? `-RM ${formatted}` : `RM ${formatted}`;
}

/**
 * Format a currency amount without the currency symbol: "1,234.56" / "-1,234.56".
 * Use when the currency is indicated by a column header or label.
 */
export function formatCurrencyValue(value) {
  const num = Number(value);
  const safe = Number.isFinite(num) ? num : 0;
  const abs = Math.abs(safe);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return safe < 0 ? `-${formatted}` : formatted;
}

/**
 * Format a number with thousands separators (no currency symbol, no forced decimals).
 */
export function formatNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('en-US');
}

const pad2 = (n) => String(n).padStart(2, '0');

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Format a date for notifications with smart relative labels:
 * - Today: "Today, 14:34"
 * - Yesterday: "Yesterday, 14:34"
 * - Within 7 days: "Monday, 14:34"
 * - Older: "15 Aug 2026, 14:34"
 */
export function formatRelativeDateTime(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';

  const now = new Date();
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

  // Check if same day
  const isSameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isSameDay) return `Today, ${time}`;

  // Check if yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) return `Yesterday, ${time}`;

  // Within 7 days: show day name
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays >= 0 && diffDays < 7) {
    return `${DAY_NAMES[d.getDay()]}, ${time}`;
  }

  // Older than 7 days: full date
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${time}`;
}

/**
 * Format a date as dd/mm/yyyy.
 * Accepts Date objects, ISO strings, and 'YYYY-MM-DD' date-only strings
 * (date-only strings are parsed without timezone shifting).
 * Empty/invalid input returns '—'.
 */
export function formatDate(value) {
  if (!value) return '—';
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/**
 * Format a date/timestamp as "dd/mm/yyyy, HH:MM" (24-hour).
 * Empty/invalid input returns '—'.
 */
export function formatDateTime(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(d)}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// Known enum values with non-generic display labels.
const ENUM_LABEL_OVERRIDES = {
  tools_equipment: 'Tools & Equipment',
};

/**
 * Humanize a DB enum/slug for display: "pending_approval" -> "Pending Approval".
 * Uses ENUM_LABEL_OVERRIDES for special cases (e.g. tools_equipment -> "Tools & Equipment").
 * Empty input returns '—'.
 */
export function formatEnumLabel(value) {
  if (value === null || value === undefined || value === '') return '—';
  const key = String(value);
  if (ENUM_LABEL_OVERRIDES[key]) return ENUM_LABEL_OVERRIDES[key];
  return key
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
